// Service worker registration wrapper.
// Sole registrar for /sw.js. Guarded against Lovable preview and dev environments.
import { toast } from "sonner";

const SW_URL = "/sw.js";
const DISMISS_KEY = "ws-install-dismissed";

function isRefusedContext(): boolean {
  if (typeof window === "undefined") return true;
  if (!import.meta.env.PROD) return true;
  if (window.self !== window.top) return true; // inside iframe (Lovable preview)
  const host = window.location.hostname;
  if (host.startsWith("id-preview--") || host.startsWith("preview--")) return true;
  if (host === "lovableproject.com" || host.endsWith(".lovableproject.com")) return true;
  if (host === "lovableproject-dev.com" || host.endsWith(".lovableproject-dev.com")) return true;
  if (host === "beta.lovable.dev" || host.endsWith(".beta.lovable.dev")) return true;
  const params = new URLSearchParams(window.location.search);
  if (params.get("sw") === "off") return true;
  return false;
}

async function unregisterMatching(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  const regs = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    regs
      .filter((r) => {
        const url = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || "";
        return url.endsWith(SW_URL);
      })
      .map((r) => r.unregister()),
  );
}

let userTriggeredUpdate = false;

function showUpdateToast(waiting: ServiceWorker) {
  toast("A new version is available", {
    duration: Infinity,
    action: {
      label: "Refresh",
      onClick: () => {
        userTriggeredUpdate = true;
        waiting.postMessage({ type: "SKIP_WAITING" });
      },
    },
  });
}

export async function registerServiceWorker(): Promise<void> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  if (isRefusedContext()) {
    await unregisterMatching().catch(() => {});
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register(SW_URL, { scope: "/" });

    // Listen for waiting worker (new version ready)
    if (registration.waiting) {
      showUpdateToast(registration.waiting);
    }

    registration.addEventListener("updatefound", () => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        if (installing.state === "installed" && navigator.serviceWorker.controller) {
          showUpdateToast(installing);
        }
      });
    });

    // Reload once when a user-triggered update takes control. Do not reload on first install.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!userTriggeredUpdate) return;
      userTriggeredUpdate = false;
      window.location.reload();
    });

    // Listen for SKIP_WAITING handoff
    navigator.serviceWorker.addEventListener("message", () => {});
  } catch (err) {
    console.warn("[pwa] SW registration failed", err);
  }
}

// beforeinstallprompt capture
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const promptListeners = new Set<(available: boolean) => void>();

export function initInstallPromptCapture() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    if (localStorage.getItem(DISMISS_KEY) === "1") return;
    deferredPrompt = e as BeforeInstallPromptEvent;
    promptListeners.forEach((fn) => fn(true));
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    promptListeners.forEach((fn) => fn(false));
  });
}

export function isInstallPromptAvailable(): boolean {
  return deferredPrompt !== null;
}

export function subscribeInstallPrompt(cb: (available: boolean) => void): () => void {
  promptListeners.add(cb);
  cb(isInstallPromptAvailable());
  return () => promptListeners.delete(cb);
}

export async function triggerInstallPrompt(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferredPrompt) return "unavailable";
  await deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  promptListeners.forEach((fn) => fn(false));
  return outcome;
}

export function dismissInstallPrompt() {
  localStorage.setItem(DISMISS_KEY, "1");
  deferredPrompt = null;
  promptListeners.forEach((fn) => fn(false));
}
